/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, type ReactNode, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import { IconButton, type IconButtonProps, LinkButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import EmailIcon from '@atlaskit/icon/core/email';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import {
	SainAction,
	useAnalytics,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics';
import { messages } from '../../messages';
import { onKnowledgeCardLinkClick } from '../analytics';
import { onEmailCopy } from '../util';

const styles = cssMap({
	wrapper: {
		'&:hover': {
			boxShadow: token('elevation.shadow.overlay'),
			borderRadius: token('radius.large'),
		},
	},
	card: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '214px',
	},
	clickableCard: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '214px',
		cursor: 'pointer',
	},
	tooltip: {
		color: token('color.text.inverse'),
		font: token('font.body.UNSAFE_small'),
	},
	tooltipContainer: {
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.small'),
		alignItems: 'center',
		zIndex: 800,
		position: 'absolute',
		left: token('space.025'),
		bottom: token('space.negative.250'),
		maxWidth: '240px',
		paddingBlock: token('space.025'),
		paddingInline: token('space.075'),
	},
});

const userNameStyles = css({
	color: token('color.text'),
	font: token('font.heading.small'),
	textAlign: 'center',
	wordBreak: 'break-all',
	display: '-webkit-box',
	overflow: 'hidden',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 1,
});

const userSubheadingStyles = css({
	color: token('color.text.subtle'),
	font: token('font.body'),
	textAlign: 'center',
	wordBreak: 'break-all',
	display: '-webkit-box',
	overflow: 'hidden',
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 1,
});

interface PersonCardProps {
	name: string;
	profilePicUrl: string;
	accountId: string;

	jobTitle?: string | null;

	profileUrl?: string | null;
	email?: string | null;
	isHoverState?: boolean;
	onCardClick?: () => void;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const tooltipOptions: IconButtonProps['tooltip'] = {
	position: 'bottom',
};

export const PersonCard = ({
	name,
	profilePicUrl,
	jobTitle,
	profileUrl,
	email,
	accountId,
	isHoverState = true,
	onCardClick,
}: PersonCardProps) => {
	const { commonAttributes } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { formatMessage } = useIntl();
	const [tipState, setTipState] = useState(false);

	const { href: profileHref, onNavigate: profileOnNavigate } = navigateToTeamsApp({
		type: 'USER',
		payload: {
			userId: accountId,
		},
		cloudId: commonAttributes.cloudId,
	});

	const Wrapper =
		!isHoverState && !!onCardClick
			? (props: { children: ReactNode }) => (
					// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
					<Box
						xcss={styles.wrapper}
						onClick={() => {
							fireSAINUsageEvent({ metCriteria: SainAction.PERSON_SELECTED, commonAttributes });
							fireAnalyticsEvent(
								onKnowledgeCardLinkClick({ ...commonAttributes, location: 'person-card-click' }),
							);
							onCardClick();
						}}
					>
						{props.children}
					</Box>
				)
			: Fragment;

	return (
		<Wrapper>
			<Flex
				direction="column"
				alignItems="center"
				gap="space.200"
				xcss={!isHoverState && !!onCardClick ? styles.clickableCard : styles.card}
			>
				<Box>
					<Avatar src={profilePicUrl} size="xlarge" />
				</Box>
				<Flex direction="column" alignItems="center" gap="space.100">
					<div css={userNameStyles}>{name}</div>
					{jobTitle && <div css={userSubheadingStyles}>{jobTitle}</div>}
				</Flex>

				<Flex gap="space.050">
					{email && (
						<IconButton
							icon={(props) => <EmailIcon {...props} color={token('color.icon')} />}
							onClick={() => {
								fireAnalyticsEvent(
									onKnowledgeCardLinkClick({
										...commonAttributes,
										location: 'person-card-copy-email',
									}),
								);
								fireSAINUsageEvent({ metCriteria: SainAction.COPY_CLICKED, commonAttributes });
								onEmailCopy(email);
							}}
							label={formatMessage(messages.knowledge_cards_copy_email_address)}
							onMouseEnter={() => setTipState(true)}
							onMouseLeave={() => setTipState(false)}
							isTooltipDisabled={isHoverState}
							tooltip={tooltipOptions}
						/>
					)}

					{profileUrl && (
						<LinkButton
							href={fg('platform-adopt-teams-nav-config') ? profileHref : profileUrl}
							target="_blank"
							onClick={(e) => {
								fireAnalyticsEvent(
									onKnowledgeCardLinkClick({
										...commonAttributes,
										location: 'person-card-view-profile',
									}),
								);
								fireSAINUsageEvent({
									metCriteria: SainAction.VIEW_PROFILE_CLICKED,
									commonAttributes,
								});
								if (fg('platform-adopt-teams-nav-config')) {
									profileOnNavigate(e);
								}
							}}
						>
							<FormattedMessage {...messages.ai_answer_who_questions_view_profile} />
						</LinkButton>
					)}
					{isHoverState && tipState && (
						<Box xcss={styles.tooltipContainer}>
							<Box xcss={styles.tooltip}>
								{formatMessage(messages.knowledge_cards_copy_email_address)}
							</Box>
						</Box>
					)}
				</Flex>
			</Flex>
		</Wrapper>
	);
};
